import { motion } from 'framer-motion';
import { Instagram, Linkedin, Mail, Phone, Rocket } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import type { Bilingual } from '../i18n/LanguageContext';
import { copy } from '../i18n/copy';
import { scrollToId } from '../utils/scroll';

interface Contact {
  icon: LucideIcon;
  label: Bilingual;
  value: string;
  href: string;
}

const contacts: Contact[] = [
  { icon: Phone, label: copy.lblPhone, value: '+962 799 670 628', href: 'tel:+962799670628' },
  {
    icon: Mail,
    label: copy.lblEmail,
    value: 'taaacademy725@gmail.com',
    href: 'mailto:taaacademy725@gmail.com',
  },
  {
    icon: Linkedin,
    label: { en: 'LinkedIn', ar: 'لينكد إن' },
    value: 'Taa Academy',
    href: 'https://www.linkedin.com/company/ta2ai/',
  },
  {
    icon: Instagram,
    label: { en: 'Instagram', ar: 'إنستغرام' },
    value: 'taa_academy',
    href: 'https://www.instagram.com/taa_academy',
  },
];

export default function Footer() {
  const { t } = useLanguage();

  return (
    <footer className="relative z-10 mt-6">
      {/* Pricing / beta band */}
      <section id="pricing" className="mx-auto max-w-6xl scroll-mt-24 px-5">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.5 }}
          className="rounded-3xl border border-amber-400/30 bg-amber-400/[0.06] p-10 text-center shadow-[0_0_80px_-30px_rgba(251,191,36,0.5)] backdrop-blur-xl"
        >
          <h2 className="text-3xl font-extrabold text-white md:text-4xl">{t(copy.pricingTitle)}</h2>
          <p className="mx-auto mt-3 max-w-2xl leading-relaxed text-slate-300">
            {t(copy.pricingText)}
          </p>
          <motion.button
            type="button"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.96 }}
            onClick={() => scrollToId('missions')}
            className="mt-7 inline-flex items-center gap-2.5 rounded-2xl bg-gradient-to-r from-amber-400 to-orange-500 px-7 py-3.5 font-extrabold text-navy-950 shadow-[0_0_40px_-10px_rgba(251,146,60,0.8)]"
          >
            <Rocket className="size-5" />
            {t(copy.startBuilding)}
          </motion.button>
        </motion.div>
      </section>

      {/* Contact */}
      <section className="mx-auto max-w-6xl px-5 py-14">
        <h2 className="text-center text-2xl font-extrabold text-white">{t(copy.contactTitle)}</h2>
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {contacts.map((contact) => {
            const Icon = contact.icon;
            const external = contact.href.startsWith('http');
            return (
              <a
                key={contact.href}
                href={contact.href}
                target={external ? '_blank' : undefined}
                rel={external ? 'noreferrer' : undefined}
                className="flex items-center gap-3.5 rounded-2xl border border-white/10 bg-white/[0.04] p-4 backdrop-blur-xl transition-[border-color,box-shadow] duration-300 hover:border-cyan-400/40 hover:shadow-[0_0_30px_-12px_rgba(34,211,238,0.6)]"
              >
                <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-cyan-400/10 text-cyan-300 ring-1 ring-cyan-400/30">
                  <Icon className="size-5" />
                </span>
                <span className="min-w-0">
                  <span className="block text-xs font-semibold uppercase tracking-wider text-slate-400">
                    {t(contact.label)}
                  </span>
                  <span className="block truncate text-sm font-semibold text-white" dir="ltr">
                    {contact.value}
                  </span>
                </span>
              </a>
            );
          })}
        </div>
      </section>

      <div className="border-t border-white/5 py-6 text-center text-sm text-slate-500">
        © {new Date().getFullYear()} Taa Academy — {t(copy.rights)}
      </div>
    </footer>
  );
}
